import { format } from "date-fns";
import type { ForecastDiagnosisStory } from "../forecast-diagnosis/forecastDiagnosisStory";
import type { ExpectedCloseV2CompanyForecast, ExpectedCloseV2DealForecast } from "../expected-close-v2/localExpectedCloseForecast";
import { getExpectedCloseV2Timing, getSelectedPeriodMonths } from "../forecast-diagnosis/forecastDiagnosisContract";

export const outlookMonthLabel = (value: string | Date | null | undefined) => {
  if (!value) return "Not available";
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? format(date, "MMMM yyyy") : "Not available";
};

// Present the existing model's month; never infer in-period timing from a missing
// expectedAfterPeriod flag and never replace V2 with a separately calculated date.
export function outlookCloseTiming(forecast: ExpectedCloseV2DealForecast | undefined, start: Date, end: Date) {
  const timing = getExpectedCloseV2Timing(forecast, getSelectedPeriodMonths(start, end), format(end, "yyyy-MM"));
  const month = timing?.expectedCloseMonth;
  if (!month) return { month: "Not available", conclusion: "Expected close timing is unavailable." };
  const label = outlookMonthLabel(`${month}-01T12:00:00`);
  if (label === "Not available") return { month: label, conclusion: "Expected close timing is unavailable." };
  const relation = month < format(start, "yyyy-MM") ? "before" : month > format(end, "yyyy-MM") ? "after" : "within";
  return { month: label, conclusion: `If won, expected to close in ${label}, ${relation} the selected period.` };
}

// These are existing model probabilities for the entire selected period.
// A positive month alone is not sufficient support for a distant acceleration.
export function getOutlookCandidateIds(artifact: ExpectedCloseV2CompanyForecast, start: Date, end: Date) {
  const first = format(start, "yyyy-MM");
  const last = format(end, "yyyy-MM");
  const terminal = new Set(artifact.terminalBuyerOutcomeDeals?.map(deal => String(deal.dealId)));
  return new Set(artifact.dealForecasts.filter(deal => {
    if (terminal.has(String(deal.dealId))) return false;
    const inPeriod = deal.wonProbabilityByMonth.filter(([month]) => month >= first && month <= last)
      .reduce((sum, [, p]) => sum + p, 0);
    const outside = deal.wonProbabilityByMonth.filter(([month]) => month < first || month > last).reduce((sum, [, p]) => sum + p, 0);
    return inPeriod > 0 && inPeriod >= outside;
  }).map(deal => String(deal.dealId)));
}

// Keep complete reviewed conditions and amounts; never split grouped impacts.
export function selectOutlookPaths(story: ForecastDiagnosisStory,
  artifact: ExpectedCloseV2CompanyForecast, start: Date, end: Date, daysRemaining: number) {
  if (daysRemaining <= 0) return [];
  const candidates = getOutlookCandidateIds(artifact, start, end);
  const used = new Set<string>();
  return (story.scenarios.find(scenario => scenario.kind === "upside")?.conditions ?? [])
    .toSorted((a, b) => (b.impact ?? 0) - (a.impact ?? 0)).flatMap(condition => {
      const ids = condition.dealIds ?? (condition.dealId ? [condition.dealId] : []);
      if (!ids.length || !condition.impact || condition.impact <= 0
        || ids.some(id => used.has(id) || !candidates.has(id))) return [];
      ids.forEach(id => used.add(id));
      return [{ ...condition, ids, impact: condition.impact }];
    });
}

export function selectOutlookDrivers(story: ForecastDiagnosisStory, minimumImpact: number,
  memberships: Array<Pick<ForecastDiagnosisStory["drivers"][number], "id" | "dealIds" | "allDealIds">> = story.drivers) {
  const byId = new Map(story.deals.map(deal => [deal.id, deal]));
  const used = new Set<string>();
  const groups: { id: string; label: string; amount: number; dealIds: string[]; kind: "exposure" | "contribution" }[] = [];
  // Reuse reviewed signal membership, but value it with current expected ARR.
  // These are exposures, never invented causal deductions or CRM differences.
  for (const [id, label] of [
    ["timing-and-deal-velocity", "Slow progression exposes expected revenue"],
    ["pipeline-momentum", "Weak activity exposes expected revenue"],
    ["other-deal-risks", "Other deal risks expose expected revenue"],
  ]) {
    const source = memberships.find(driver => driver.id === id);
    const ids = [...new Set(source?.allDealIds ?? source?.dealIds ?? [])]
      .filter(id => !used.has(id) && (byId.get(id)?.evidenceContribution ?? 0) > 0)
      .toSorted((a, b) => byId.get(b)!.evidenceContribution - byId.get(a)!.evidenceContribution);
    const amount = ids.reduce((sum, id) => sum + byId.get(id)!.evidenceContribution, 0);
    if (amount < minimumImpact || !ids.length) continue;
    ids.forEach(id => used.add(id));
    groups.push({ id, label, amount, dealIds: ids, kind: "exposure" });
  }
  const contributors = story.deals.filter(deal => !used.has(deal.id) && deal.evidenceContribution > 0)
    .toSorted((a, b) => b.evidenceContribution - a.evidenceContribution).slice(0, 3);
  if (contributors.reduce((sum, deal) => sum + deal.evidenceContribution, 0) >= minimumImpact && contributors.length) groups.push({ id: "contribution", label: "Largest other expected contributions",
    amount: contributors.reduce((sum, deal) => sum + deal.evidenceContribution, 0),
    dealIds: contributors.map(deal => deal.id), kind: "contribution" });
  return groups;
}

// The legacy chart starts at last month even for "This year". SoS already
// supplies a complete selected-period series with a context row. Select that
// existing series at the caller boundary, preserving the position helper's
// leading-context convention. No monthly arithmetic changes.
export function withOutlookPeriodRows<T extends { forecastChartData: unknown[]; forecastChartDataWithContext: unknown[] }>(forecast: T, period: string): T {
  return period === "this_year" || period === "next_6_months"
    ? { ...forecast, forecastChartData: forecast.forecastChartDataWithContext }
    : forecast;
}
